import { useQuery } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

export interface BmaSiteSummary {
  id: string;
  name: string;
  address: string;
  postcode: string;
  // Organisations (siteOwnerships) + target size are surfaced on the
  // list payload so the New Project flow can show them without a
  // per-site read - BMA's /site/<id> ownership gate denies the
  // cross-account RO token, so the list is the only place the agents
  // service can see a site's orgs.
  organisations: BmaSiteOrganisation[];
  target_system_size_kwp: number | null;
  // The project that's already linked to this BMA site, or
  // null if the site is free. Lets the FE warn before the operator
  // picks a site that's already taken.
  linked_project_id: string | null;
}

export interface BmaSiteOrganisation {
  bma_org_id: string;
  name: string;
  type: string;
}

export interface BmaSiteDetail {
  id: string;
  name: string;
  address: string;
  postcode: string;
  mpans: string[];
  target_system_size_kwp: number | null;
  organisations: BmaSiteOrganisation[];
  linked_project_id: string | null;
}

const LIST_KEY = QK.bmaSitesList;
const GET_KEY = QK.bmaSiteGet;
const ORGS_LIST_KEY = QK.bmaOrgsList;

export interface BmaOrgSummary {
  bma_org_id: string;
  name: string;
  type: string;
}

export const useBmaSitesList = (options?: { enabled?: boolean }) => {
  const http = useAgentsHttp();
  return useQuery<BmaSiteSummary[]>(
    [LIST_KEY],
    async () => {
      const resp = await http<{ sites: BmaSiteSummary[] }>("/bma/sites/list", {});
      return resp.sites;
    },
    {
      enabled: options?.enabled ?? true,
      refetchOnWindowFocus: false,
      // BMA's /organisation list is admin-side data; it changes
      // rarely. 5 min cache keeps the picker snappy without going
      // stale on real ops.
      staleTime: 5 * 60_000,
    },
  );
};

export const useBmaOrgsList = () => {
  const http = useAgentsHttp();
  return useQuery<BmaOrgSummary[]>(
    [ORGS_LIST_KEY],
    async () => {
      const resp = await http<{ orgs: BmaOrgSummary[] }>("/bma/orgs/list", {});
      return resp.orgs;
    },
    {
      refetchOnWindowFocus: false,
      // Production org list changes rarely; 5 min keeps the picker
      // snappy without going stale on real ops.
      staleTime: 5 * 60_000,
    },
  );
};

export const useBmaSite = (site_id: string | null | undefined) => {
  const http = useAgentsHttp();
  return useQuery<BmaSiteDetail>(
    [GET_KEY, site_id ?? null],
    async () => {
      const resp = await http<{ site: BmaSiteDetail }>("/bma/sites/get", {
        site_id,
      });
      return resp.site;
    },
    {
      enabled: typeof site_id === "string" && site_id.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  );
};
